(() => {
  const tag = 'owned-items-selector';
  if (customElements.get(tag)) return;

  class OwnedItemsSelector extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.form = this.querySelector('[data-selector-form]');
      if (!this.form) return;
      this.rows = [...this.querySelectorAll('[data-item]')];
      this.button = this.querySelector('[data-add]');
      this.subtotal = this.querySelector('[data-subtotal]');
      this.message = this.querySelector('[data-selection-message]');
      this.result = this.querySelector('[data-result]');
      this.cartLink = this.querySelector('[data-cart-link]');
      this.buttonLabel ??= this.button.textContent;
      this.state ??= 'idle';
      this.events = new AbortController();
      const listenerOptions = { signal: this.events.signal };
      this.form.addEventListener('change', () => this.render(), listenerOptions);
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.addItems();
      }, listenerOptions);
      try {
        this.money = new Intl.NumberFormat(this.dataset.locale || 'en-US', {
          style: 'currency', currency: this.dataset.currency || 'USD'
        });
      } catch {
        this.money = null;
      }
      this.render();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
    }

    selection() {
      const variants = new Map();
      let needed = 0;
      let missing = false;
      let unavailable = false;
      for (const row of this.rows) {
        if (row.querySelector('[data-owned]').checked) continue;
        needed += 1;
        const select = row.querySelector('[data-variant]');
        const option = select.options[select.selectedIndex];
        const purchasable = [...select.options].some(item => item.value && item.dataset.available === 'true');
        if (!purchasable) {
          unavailable = true;
          continue;
        }
        if (!option?.value) {
          missing = true;
          continue;
        }
        const id = Number(option.value);
        const price = Number(option.dataset.price);
        if (option.dataset.available !== 'true' || !Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(price) || price < 0) {
          unavailable = true;
          continue;
        }
        // Repeated merchant blocks must not accidentally buy the same variant twice.
        variants.set(id, { id, quantity: 1, price });
      }
      return {
        needed, missing, unavailable,
        items: [...variants.values()],
        valid: needed > 0 && !missing && !unavailable && variants.size > 0
      };
    }

    render() {
      const locked = this.state !== 'idle';
      for (const row of this.rows) {
        const owned = row.querySelector('[data-owned]');
        const select = row.querySelector('[data-variant]');
        const option = select.options[select.selectedIndex];
        const available = [...select.options].some(item => item.value && item.dataset.available === 'true');
        row.classList.toggle('ois__item--excluded', owned.checked);
        owned.disabled = locked;
        select.disabled = owned.checked || locked || !available;
        row.querySelector('[data-options]').hidden = owned.checked;
        row.querySelector('[data-price-display]').textContent = available ? (option?.dataset.money || '') : this.dataset.unavailableLabel;
      }
      const choice = this.selection();
      const cents = choice.items.reduce((sum, item) => sum + item.price, 0);
      this.subtotal.textContent = this.money ? this.money.format(cents / 100) : (cents / 100).toFixed(2) + ' ' + this.dataset.currency;
      this.message.textContent = !choice.needed ? this.dataset.allOwned
        : choice.unavailable ? this.dataset.unavailable
        : choice.missing ? this.dataset.chooseOption : '';
      this.button.disabled = locked || !choice.valid;
      this.button.textContent = this.state === 'adding' ? this.dataset.loading : this.buttonLabel;
      this.form.setAttribute('aria-busy', String(this.state === 'adding'));
    }

    async addItems() {
      if (this.state !== 'idle') return;
      const choice = this.selection();
      if (!choice.valid) {
        this.render();
        return;
      }
      let endpoint;
      try {
        endpoint = new URL(this.dataset.cartAddUrl, window.location.origin);
        if (endpoint.origin !== window.location.origin || !endpoint.pathname.endsWith('/cart/add.js')) throw new Error('Invalid cart route');
      } catch {
        this.showResult('uncertain', this.dataset.error);
        return;
      }

      this.state = 'adding';
      this.result.hidden = true;
      this.render();
      const request = new AbortController();
      const timeout = window.setTimeout(() => request.abort(), 15000);
      try {
        const response = await fetch(endpoint.href, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: choice.items.map(({ id, quantity }) => ({ id, quantity })) }),
          signal: request.signal
        });
        const body = await response.json();
        const received = new Set(Array.isArray(body.items) ? body.items.map(item => Number(item.variant_id ?? item.id)) : []);
        if (!response.ok || !choice.items.every(item => received.has(item.id))) throw new Error('Cart result requires review');
        this.showResult('added', this.dataset.success);
      } catch {
        // Even an error or timeout can follow a partial server-side addition.
        // Freeze this attempt and direct the buyer to review the real cart.
        this.showResult('uncertain', this.dataset.error);
      } finally {
        window.clearTimeout(timeout);
      }
    }

    showResult(state, text) {
      this.state = state;
      this.result.hidden = false;
      this.result.textContent = text;
      this.cartLink.hidden = false;
      this.render();
      if (this.isConnected) this.cartLink.focus();
    }
  }

  customElements.define(tag, OwnedItemsSelector);
})();
